import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import {
	DndContext,
	useDraggable,
	useDroppable,
	PointerSensor,
	useSensor,
	useSensors,
	type DragStartEvent,
	type DragEndEvent,
	DragOverlay,
} from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { Command, CommandStatus } from "../../../types/command.types";
import { useInfiniteCommands } from "../../../hooks/commands.hooks";
import {
	changeCommandStatus,
	useBulkChangeCommandStatus,
} from "../../../mutations/command.mutations";
import { Box, Flex, Text, useDisclosure } from "@chakra-ui/react";
import { cn } from "../../../lib/utils";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
	BrandTabButtonClear,
	BrandTabSwitcher,
} from "../../../components/BrandTabSwitcher";
import { useOrdersKanbanStore } from "../../../stores/kanban.store";
import BrandFlag from "../../../components/icons/BrandFlag";
import BuildingOutlinedIcon from "../../../components/icons/BuildingOutlinedIcon";
import CommandTypeBadge from "../../../components/CommandTypeBadge";
import type { OrderStoreState } from "../../../stores/order.store";
import {
	CirclePause,
	CirclePlay,
	CircleX,
	Ellipsis,
	LoaderCircle,
	UserPen,
} from "lucide-react";
import { format } from "date-fns";
import InlineUserAvatar from "../../../components/InlineUserAvatar";
import BrandCirclePause from "../../../components/icons/BrandCirclePause";
import OrdersSubToolbar from "../components/OrdersSubToolbar";
import {
	useInfinitePurchaseOrder,
	usePurchaseOrder,
} from "../../../hooks/purchase_order.hooks";
import { PurchaseOrder } from "../../../types/purchase_orders.types";
import { Link } from "react-router-dom";
import BrandFilePlus from "../../../components/icons/BrandFilePlusIcon";
import OrderStatusBadge from "../../../components/OrderStatusBadge";
import {
	autoUpdate,
	flip,
	FloatingPortal,
	offset,
	shift,
	useFloating,
} from "@floating-ui/react";
import BrandZapFastIcon from "../../../components/icons/BrandZapFastIcon";
import DeleteOrderModal from "../../../components/modals/DeleteOrderModal";
import ChangeManagerOrderModal from "../../../components/modals/ChangeManagerOrderModal";
import BrandZapOffIcon from "../../../components/icons/BrandZapOffIcon";
import {
	fetchCommandsPaginated,
	toggleGroupCommand,
} from "../../../queries/command.queries";
import { useFile } from "../../../hooks/file.hooks";
import { useMaterialByLabel } from "../../../hooks/material.hooks";
import OrderSelectCenter from "../filters/OrdersSelectCenter";
import OrdersSearch from "../filters/OrdersSearch";
import OrdersResetFilters from "../filters/OrdersResetFilters";
import { useTickets } from "../../../hooks/tickets.hooks";
import { useSimpleToast } from "../../../hooks/toast.hooks";
import Tooltip from "../../../components/Tooltip";
import { socket } from "../../../socket";

type OrderCard = { command: Command };

type CommandStatusChangedPayload = {
	commandId: number;
	prevStatus: CommandStatus | "HALTED" | "RESUMED";
	nextStatus: CommandStatus | "HALTED" | "RESUMED";
	currentStatus: CommandStatus;
	halted: boolean;
};

const isCommandStatus = (value: unknown): value is CommandStatus => {
	return Object.values(CommandStatus).includes(value as CommandStatus);
};

const hasPurchaseOrderId = (
	purchaseOrder: PurchaseOrder,
): purchaseOrder is PurchaseOrder & { id: number } => {
	return typeof purchaseOrder.id === "number";
};

const findSideMaterialOption = ({
	side,
	draft,
}: {
	side: "left" | "right";
	draft: OrderStoreState;
}) => {
	const hasSide = side === "right" ? draft.hasRightEar : draft.hasLeftEar;
	if (!hasSide) return null;
	const variants = draft.variants;
	if (!Array.isArray(variants) || variants.length === 0) return null;
	const materialVariant = variants.find((variant) =>
		variant.label.startsWith("Matière"),
	);
	if (!materialVariant) return null;
	const form = side === "right" ? draft?.rightEarForm : draft?.leftEarForm;
	return form[materialVariant.id];
};

const findSideFinishingOption = ({
	side,
	draft,
}: {
	side: "left" | "right";
	draft: OrderStoreState;
}) => {
	const hasSide = side === "right" ? draft.hasRightEar : draft.hasLeftEar;
	if (!hasSide) return null;
	const variants = draft.variants;
	if (!Array.isArray(variants) || variants.length === 0) return null;
	const materialVariant = variants.find((variant) =>
		variant.label.startsWith("Finition"),
	);
	if (!materialVariant) return null;
	const form = side === "right" ? draft?.rightEarForm : draft?.leftEarForm;
	return form[materialVariant.id];
};

const getCommandMaterialType = (
	command: Command,
): "SILICONE" | "RESIN" | null => {
    if (command?.material) return command?.material
	const draft = command.draft;
	if (!draft?.variants?.length) return null;
	const materialVariant = draft.variants.find((v) =>
		v.label.startsWith("Matière"),
	);
	if (!materialVariant) return null;
	const form = draft.rightEarForm ?? draft.leftEarForm;
	const option = form?.[materialVariant.id];
	if (!option?.label) return null;
	return option.label.includes("Résine") ? "RESIN" : "SILICONE";
};

const OrdersKanbanCardSide = ({
	side,
	draft,
}: {
	side: "left" | "right";
	draft: OrderStoreState;
}) => {
	const hasSide = side === "right" ? draft.hasRightEar : draft.hasLeftEar;
	const className = cn(
		"flex flex-row justify-between items-center h-5 px-2",
		side === "right" ? "bg-error-50" : "bg-brand-50",
	);
	const draftMaterial = findSideMaterialOption({ side, draft });
	const finishing = findSideFinishingOption({ side, draft });
	const material = useMaterialByLabel(draftMaterial?.label);
	const materialFileState = useFile({ id: material?.fileId });

	if (!hasSide) return null;
	return (
		<div className={className}>
			<Flex direction="row" alignItems="center" gap={2}>
				<Tooltip content={material?.label}>
					{materialFileState.data?.url && (
						<img
							className="size-5 aspect-square"
							src={materialFileState.data?.url}
							alt={`materiel ${draftMaterial?.uuid}`}
						/>
					)}
				</Tooltip>
				{draftMaterial && (
					<Text fontSize={"12px"} fontWeight={500}>
						{draftMaterial.label.includes("Résine") ? "Résine" : "Silicone"}
					</Text>
				)}
			</Flex>
			{finishing && (
				<Text fontSize={"12px"} fontWeight={500}>
					{finishing.label}
				</Text>
			)}
		</div>
	);
};

const OrdersKanbanCardContextMenu = ({ command }: { command: Command }) => {
	const disclosureState = useDisclosure();
	const deleteCommandModalDisclosure = useDisclosure();
	const changeCommandManagerModalDisclosure = useDisclosure();
	const floatingState = useFloating({
		open: disclosureState.isOpen,
		placement: "bottom-end",
		onOpenChange: (open: boolean) =>
			open ? disclosureState.onOpen() : disclosureState.onClose(),
		middleware: [offset(4), shift(), flip()],
		whileElementsMounted: autoUpdate,
	});

	const queryClient = useQueryClient();
	const commandStatusMutator = useMutation(changeCommandStatus, {
		onSuccess: async () => {
			await queryClient.invalidateQueries();
		},
	});
	const toggleGroupCommandMutator = useMutation(toggleGroupCommand, {
		onSuccess: async () => {
			await queryClient.invalidateQueries();
		},
	});

	const actionHaltCommand = () => {
		if (commandStatusMutator.isLoading) return;

		commandStatusMutator.mutate({
			id: command.id,
			status: "HALTED",
			reason: "",
			explanation: "",
		});
	};

	const actionResumeCommand = () => {
		if (commandStatusMutator.isLoading) return;

		commandStatusMutator.mutate({
			id: command.id,
			status: "RESUMED",
			reason: "",
			explanation: "",
		});
	};

	const actionGroupCommand = () => {
		if (toggleGroupCommandMutator.isLoading) return;

		toggleGroupCommandMutator.mutate(command.id);
	};

	const handleActions = (action?: () => void) => {
		return () => {
			disclosureState.onClose();
			action?.();
		};
	};

	return (
		<>
			<Box
				ref={floatingState.refs.setReference}
				position="absolute"
				p={1}
				top={2}
				right={2}
				backgroundColor="white"
				rounded="lg"
				border="1px solid"
				borderColor="gray.200"
				_hover={{
					backgroundColor: "gray.50",
				}}
				onClick={(e) => {
					e.stopPropagation();
					e.preventDefault();
					disclosureState.onToggle();
				}}
			>
				<Ellipsis />
			</Box>
			{disclosureState.isOpen && (
				<FloatingPortal>
					<Box
						w="300px"
						p={2}
						backgroundColor="white"
						border="1px solid"
						borderColor="gray.200"
						rounded="xl"
						ref={floatingState.refs.setFloating}
						style={floatingState.floatingStyles}
						onClick={(e) => {
							e.stopPropagation();
							e.preventDefault();
						}}
					>
						<Flex direction="column">
							{command.groupExpectedShippingDate === null && (
								<Flex
									p={1}
									_hover={{ backgroundColor: "gray.50" }}
									alignItems="center"
									gap={2}
									onClick={handleActions(actionGroupCommand)}
								>
									<BrandZapFastIcon w="16px" h="16px" />
									Ajouter au regroupement
								</Flex>
							)}
							{command.groupExpectedShippingDate !== null && (
								<Flex
									p={1}
									_hover={{ backgroundColor: "gray.50" }}
									alignItems="center"
									gap={2}
									onClick={handleActions(actionGroupCommand)}
								>
									<BrandZapOffIcon w="16px" h="16px" />
									Enlever du regroupement
								</Flex>
							)}
							<Flex
								p={1}
								_hover={{ backgroundColor: "gray.50" }}
								alignItems="center"
								gap={2}
								onClick={() => changeCommandManagerModalDisclosure.onOpen()}
							>
								<UserPen size="16px" />
								Changer l'assignation
							</Flex>
							{command.haltedAt == null ? (
								<Flex
									p={1}
									_hover={{ backgroundColor: "gray.50" }}
									alignItems="center"
									gap={2}
									onClick={handleActions(actionHaltCommand)}
								>
									<CirclePause size="16px" />
									Mettre en pause
								</Flex>
							) : (
								<Flex
									p={1}
									_hover={{ backgroundColor: "gray.50" }}
									alignItems="center"
									gap={2}
									onClick={handleActions(actionResumeCommand)}
								>
									<CirclePlay size="16px" />
									Relancer la commande
								</Flex>
							)}
							<Flex
								p={1}
								_hover={{ backgroundColor: "gray.50" }}
								alignItems="center"
								gap={2}
								textColor="red.700"
								onClick={() => deleteCommandModalDisclosure.onOpen()}
							>
								<CircleX size="16px" />
								Annuler la commande
							</Flex>
							<DeleteOrderModal
								orderId={command.id}
								{...deleteCommandModalDisclosure}
								onClose={() => {
									deleteCommandModalDisclosure.onClose();
									disclosureState.onClose();
								}}
							></DeleteOrderModal>
							<ChangeManagerOrderModal
								orderId={command.id}
								{...changeCommandManagerModalDisclosure}
								onClose={() => {
									changeCommandManagerModalDisclosure.onClose();
									disclosureState.onClose();
								}}
							/>
						</Flex>
					</Box>
				</FloatingPortal>
			)}
		</>
	);
};

const OrdersKanbanCard = ({
	card,
	isSelected,
	onToggleSelect,
	cardRef,
	isDragging,
	style = {},
}: {
	card: OrderCard;
	isSelected: boolean;
	onToggleSelect: () => void;
	cardRef?: (node: HTMLElement | null) => void;
	isDragging?: boolean;
	style?: object;
}) => {
	const isInInjectingColumn = card.command.status === CommandStatus.INJECTING;
	const materialType = getCommandMaterialType(card.command);
	const canDrag = !isInInjectingColumn || materialType === "SILICONE";
	const { attributes, listeners, setNodeRef, transform } = useDraggable({
		id: card.command.id,
		disabled: !canDrag,
	});
	const { setFilterCenterId } = useOrdersKanbanStore();
	const tickets = useTickets(
		1,
		9,
		undefined,
		{
			commandId: card.command.id.toString(),
		},
		undefined,
	);

	const handleCenterClick = (e: React.MouseEvent<HTMLDivElement>) => {
		e.stopPropagation();
		e.preventDefault();
		setFilterCenterId(card.command.centerId.toString());
	};

	const dragStyle = {
		...style,
		transform: isDragging
			? undefined
			: CSS.Transform.toString(
					transform
						? {
								...transform,
								scaleX: 1,
								scaleY: 1,
							}
						: null,
				),
	};

	const handleRef = useCallback(
		(node: HTMLElement | null) => {
			setNodeRef(node);
			if (cardRef) {
				cardRef(node);
			}
		},
		[setNodeRef, cardRef],
	);

	const handleClick = useCallback(
		(e: React.MouseEvent) => {
			// Prevent drag from starting on click
			e.stopPropagation();
			if (e.shiftKey) {
				onToggleSelect();
			} else {
				const url = new URL(
					`/orders/${card.command.id}`,
					window.location.origin,
				);
				window.open(url.toString(), "_blank", "noopener,noreferrer");
			}
		},
		[onToggleSelect, card.command],
	);
	const contextMenuState = useDisclosure();

	const draft = card.command.draft;
	const shouldShowShippingDate =
		card.command.status !== CommandStatus.WAITING_FOR_RECEPTION;
	return (
		<Flex
			data-card-id={card.command.id}
			className={cn(
				"w-full relative cursor-pointer transition-colors bg-white select-none rounded-lg flex-col gap-2 p-2",
				isSelected
					? "border-2! border-blue-500! bg-brand-25"
					: "border! border-gray-200!",
				isDragging && "opacity-30 pointer-events-none cursor-not-allowed",
				!canDrag && "cursor-default",
			)}
			style={dragStyle}
			ref={handleRef}
			onClick={handleClick}
			onMouseEnter={contextMenuState.onOpen}
			onMouseLeave={contextMenuState.onClose}
			{...(canDrag ? { ...listeners, ...attributes } : {})}
		>
			<div className="flex flex-row items-center justify-between">
				<div className="flex flex-row items-center gap-2">
					{shouldShowShippingDate && (
						<Text fontSize="12px" fontWeight={500}>
							{format(
								new Date(
									card.command.groupExpectedShippingDate ??
										card.command.expectedShippingDate,
								),
								"dd/MM/yy",
							)}
						</Text>
					)}
					{card.command.isPriority ? (
						<BrandFlag width="15px" height="18px" className="text-error-600!" />
					) : null}
					{card.command.groupExpectedShippingDate ? (
						<BrandZapFastIcon
							width="18px"
							height="18px"
							fill="#1b58f5"
							className="text-brand-600!"
						/>
					) : null}
				</div>
				<div className="flex flex-row gap-1">
					{card.command.haltedAt != null && (
						<div className="rounded-full px-2 bg-orange-500 flex flex-row items-center gap-1">
							<BrandCirclePause w="12px" h="12px" className="text-white!" />
							<Text fontSize="12px" fontWeight={500} className="text-white">
								En pause
							</Text>
						</div>
					)}
					{Array.isArray(tickets?.data) && tickets.data.length > 0 && (
						<Box className="bg-error-600 text-white rounded-full! size-5! flex items-center justify-center">
							<Text fontSize="12px" fontWeight={500}>
								{tickets.data.length}
							</Text>
						</Box>
					)}
				</div>
			</div>
			<Box
				className="flex flex-row items-center gap-2"
				onClick={handleCenterClick}
			>
				<BuildingOutlinedIcon w="12px" h="12px" className="text-brand-600!" />
				<Text fontSize="12px" fontWeight={500} className="text-brand-600">
					{card.command.centerId}
				</Text>
				<Text fontSize="12px" fontWeight={500} className="text-brand-600">
					{card.command.center.name}
				</Text>
			</Box>
			<div className="flex flex-row items-center gap-2">
				<Text
					fontSize="14px"
					fontWeight={500}
					className="text-brand-700 underline lowercase"
				>
					{card.command.exposedId}-
					{card.command.patient?.firstName?.replace(" ", "_")}-
					{card.command.patient?.lastName?.replace(" ", "_")}
				</Text>
			</div>
			<div className="flex flex-row items-center gap-2">
				<CommandTypeBadge type={card.command.type}></CommandTypeBadge>
			</div>
			{draft && <OrdersKanbanCardSide side="right" draft={draft} />}
			{draft && <OrdersKanbanCardSide side="left" draft={draft} />}
			{card.command.modelerId && (
				<InlineUserAvatar
					id={card.command.modelerId}
					size="xs"
					w="20px"
					h="20px"
					fontSize="14px"
					fontWeight={400}
				/>
			)}
			{contextMenuState.isOpen && (
				<OrdersKanbanCardContextMenu command={card.command} />
			)}
		</Flex>
	);
};

const OrdersKanbanOrdersColumn = ({
	status,
	selectedIds,
	onToggleSelect,
	onCardRef,
	onCommandData,
	draggedIds,
}: {
	status: CommandStatus;
	selectedIds: Set<number>;
	onToggleSelect: (id: number) => void;
	onCardRef?: (id: number, node: HTMLElement | null) => void;
	onCommandData?: (command: Command) => void;
	draggedIds?: number[];
}) => {
	const { setNodeRef } = useDroppable({
		id: status,
	});
	const {
		search,
		filters: { centerId, modelerId, type, material, halted },
	} = useOrdersKanbanStore();

	const filters: { [key: string]: string } = { status };
	if (centerId) {
		filters.centerId = centerId;
	}
	if (modelerId) {
		filters.modelerId = modelerId;
	}
	if (type) {
		filters.type = type;
	}
	if (material) {
		filters.material = material;
	}
	if (halted) {
		filters.halted = halted ? "true" : "false";
	}
	const infiniteCommands = useInfiniteCommands({
		search,
		filters,
		sortBy: ["expectedShippingDate"],
	});
	const haltedCommandsCountState = useQuery({
		queryKey: ["commands", "count", "halted", search, filters],
		queryFn: async () => {
			const response = await fetchCommandsPaginated(
				1,
				1,
				search,
				{
					...filters,
					halted: "true",
				},
				["expectedShippingDate"],
			);
			return response.meta.total;
		},
	});

	// Register command data when commands are loaded
	useEffect(() => {
		if (infiniteCommands?.data && onCommandData) {
			const pages = infiniteCommands?.data.pages;

			pages.forEach((page) => {
				page.data.forEach((command) => {
					onCommandData(command);
				});
			});
		}
	}, [infiniteCommands?.data, onCommandData]);
	const totalCommandsInColumn =
		infiniteCommands.data?.pages?.[0]?.meta?.total ?? 0;
	const haltedCommandsInColumn = haltedCommandsCountState.data ?? 0;

	if (infiniteCommands.isLoading) {
		return (
			<div
				className="flex flex-col gap-2 p-2 min-h-[82vh] min-w-80 w-80 bg-zinc-100 rounded-lg"
				ref={setNodeRef}
			>
				<Flex>
					<OrderStatusBadge status={status} halted={false} />
				</Flex>
				<div className="flex flex-row justify-center">
					<LoaderCircle className="animate-spin" />
				</div>
			</div>
		);
	}
	return (
		<div
			className="flex flex-col gap-2 p-2 min-h-[82vh] min-w-80 w-80 bg-zinc-100 rounded-lg"
			ref={setNodeRef}
		>
			<Flex alignItems="center" gap={2}>
				<OrderStatusBadge status={status} halted={false} />
				<Text fontSize="12px" fontWeight={500}>
					{totalCommandsInColumn}
				</Text>
				{haltedCommandsInColumn > 0 && (
					<Flex
						gap={1}
						px={2}
						w="fit-content"
						alignItems="center"
						rounded="full"
						backgroundColor={"#F97316"}
						borderColor={"#EA580C"}
						textColor={"white"}
					>
						<BrandCirclePause w={3} h={3} />
						<Text fontSize="12px" fontWeight={500}>
							{haltedCommandsInColumn}
						</Text>
					</Flex>
				)}
			</Flex>
			{infiniteCommands?.data?.pages
				.flatMap((p) => p.data)
				.map((command) => (
					<OrdersKanbanCard
						key={command.id}
						card={{ command }}
						isSelected={selectedIds.has(command.id)}
						onToggleSelect={() => onToggleSelect(command.id)}
						cardRef={
							onCardRef ? (node) => onCardRef(command.id, node) : undefined
						}
						isDragging={draggedIds?.includes(command.id)}
					/>
				))}
			<Flex justifyContent="center">
				<Box
					onClick={() => {
						if (!infiniteCommands.hasNextPage) return;
						if (infiniteCommands.isFetching) return;

						infiniteCommands.fetchNextPage();
					}}
				>
					{infiniteCommands.isFetchingNextPage ? (
						<LoaderCircle className="animate-spin" />
					) : infiniteCommands.hasNextPage ? (
						<Text>Voir plus</Text>
					) : null}
				</Box>
			</Flex>
		</div>
	);
};

const OrdersKanbanPurchaseOrdersCard = ({
	purchase_order,
}: {
	purchase_order: PurchaseOrder;
}) => {
	const state = usePurchaseOrder({ id: purchase_order.id });
	const commands = state.data?.commands;
	const hasCommands = Array.isArray(commands) && commands.length > 0;

	if (state.data === undefined || state.data === null) return null;
	return (
		<Flex
			className={cn(
				"relative w-full cursor-pointer transition-colors bg-white select-none rounded-lg flex-col gap-2 p-2",
				"border! border-gray-200!",
			)}
		>
			{hasCommands && (
				<div className="flex flex-row items-center gap-2">
					<Text fontSize="12px" fontWeight={500}>
						{format(new Date(commands[0].expectedShippingDate), "dd/MM/yy")}
					</Text>
				</div>
			)}
			{hasCommands && (
				<Box className="flex flex-row items-center gap-2">
					<BuildingOutlinedIcon w="12px" h="12px" className="text-brand-600!" />
					<Text fontSize="12px" fontWeight={500} className="text-brand-600">
						{state.data.center.id}
					</Text>
					<Text fontSize="12px" fontWeight={500} className="text-brand-600">
						{state.data.center.name}
					</Text>
				</Box>
			)}

			{state.data.createdAt && (
				<Box className="flex flex-row items-center gap-2">
					<Link to={`/orders/deliverynote/${state.data.id}/`}>
						<Text fontSize="12px" fontWeight={500} className="text-brand-600">
							BL{format(new Date(state.data.createdAt), "yyyy")}
							{state.data.id}
						</Text>
					</Link>
					<BrandFilePlus w="12px" h="12px" className="text-gray-500!" />
					<Text fontSize="12px" fontWeight={500} className="text-gray-700">
						{format(new Date(state.data.createdAt), "dd MMMM yyyy")}
					</Text>
				</Box>
			)}

			{state.data.trackingId && (
				<Box className="flex flex-row items-center gap-2">
					<Text fontSize="12px" fontWeight={500} className="text-gray-700">
						<a
							href={`https://www.ups.com/track?tracknum=${state.data.trackingId}`}
							target="_blank"
						>
							{state.data.trackingId}
						</a>
					</Text>
				</Box>
			)}

			{hasCommands && (
				<div className="absolute top-2 right-2 bg-gray-200 w-6 h-6 rounded-md text-center">
					{commands.length}
				</div>
			)}
		</Flex>
	);
};

const OrdersKanbanPurchaseOrdersColumn = ({
	status,
}: {
	status: CommandStatus.IN_DELIVERY | CommandStatus.PARCEL_PREPARED;
}) => {
	const { filters } = useOrdersKanbanStore();
	const sortBy = useMemo(() => ["-created_at"], []);
	const state = useInfinitePurchaseOrder({
		filters: {
			status,
			...(filters.centerId ? { centerId: filters.centerId } : {}),
		},
		sortBy,
	});
	const purchaseOrders = useMemo(() => {
		const uniqueById = new Map<number, PurchaseOrder & { id: number }>();
		state.data?.pages.forEach((page) => {
			page.data.forEach((purchaseOrder) => {
				if (!hasPurchaseOrderId(purchaseOrder)) return;
				if (!uniqueById.has(purchaseOrder.id)) {
					uniqueById.set(purchaseOrder.id, purchaseOrder);
				}
			});
		});
		return Array.from(uniqueById.values());
	}, [state.data?.pages]);

	if (state.isLoading) {
		return (
			<div className="flex flex-col gap-2 p-2 min-h-[82vh] min-w-80 w-80 bg-zinc-100 rounded-lg">
				<Flex>
					<OrderStatusBadge status={status} halted={false} />
				</Flex>
				<div className="flex flex-row justify-center">
					<LoaderCircle className="animate-spin" />
				</div>
			</div>
		);
	}
	return (
		<div className="flex flex-col gap-2 p-2 min-h-[82vh] min-w-80 w-80 bg-zinc-100 rounded-lg">
			<Flex>
				<OrderStatusBadge status={status} halted={false} />
			</Flex>
			<Flex direction="column" gap={2}>
				{purchaseOrders.map((po) => (
					<OrdersKanbanPurchaseOrdersCard key={po.id} purchase_order={po} />
				))}
			</Flex>
			<Flex justifyContent="center">
				<Box
					onClick={() => {
						if (!state.hasNextPage) return;
						if (state.isFetching) return;

						state.fetchNextPage();
					}}
				>
					{state.isFetchingNextPage ? (
						<LoaderCircle className="animate-spin" />
					) : state.hasNextPage ? (
						<Text>Voir plus</Text>
					) : null}
				</Box>
			</Flex>
		</div>
	);
};

const OrdersKanbanToolbar = () => {
	const {
		scope,
		setScope,
		search,
		setSearch,
		filters: { centerId },
		setFilterCenterId,
	} = useOrdersKanbanStore();

	return (
		<Flex className="justify-between">
			<Flex gap={2}>
				<OrderSelectCenter id={centerId} setId={setFilterCenterId} />
				<OrdersSearch search={search} setSearch={setSearch} />
			</Flex>
			<Flex alignItems={"center"} gap={2}>
				<OrdersResetFilters />
				<BrandTabSwitcher>
					<BrandTabButtonClear
						active={scope === "global"}
						onClick={() => setScope("global")}
					>
						Globale
					</BrandTabButtonClear>
					<BrandTabButtonClear
						active={scope === "secretariat"}
						onClick={() => setScope("secretariat")}
					>
						Secrétariat
					</BrandTabButtonClear>
					<BrandTabButtonClear
						active={scope === "modeling"}
						onClick={() => setScope("modeling")}
					>
						Pôle 3D
					</BrandTabButtonClear>
					<BrandTabButtonClear
						active={scope === "lab"}
						onClick={() => setScope("lab")}
					>
						Laboratoire
					</BrandTabButtonClear>
				</BrandTabSwitcher>
			</Flex>
		</Flex>
	);
};

const OrdersKanbanView = () => {
	const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
	const [draggedIds, setDraggedIds] = useState<number[]>([]);
	const [activeId, setActiveId] = useState<number | null>(null);
	const dragStartRef = useRef<number | null>(null);
	const queryClient = useQueryClient();

	// Selection box state
	const [isSelecting, setIsSelecting] = useState(false);
	const [selectionBox, setSelectionBox] = useState<{
		startX: number;
		startY: number;
		endX: number;
		endY: number;
	} | null>(null);
	const containerRef = useRef<HTMLDivElement | null>(null);
	const cardRefs = useRef<Map<number, HTMLElement>>(new Map());
	const commandDataRef = useRef<Map<number, Command>>(new Map());
	const isDraggingRef = useRef(false);
	const selectionStartRef = useRef<{ x: number; y: number } | null>(null);

	const { mutate: bulkChangeStatus } = useBulkChangeCommandStatus();
	const toast = useSimpleToast();

	const { getScopeColumns, scope } = useOrdersKanbanStore();

	const invalidateCommandColumns = useCallback(
		(statuses: CommandStatus[]) => {
			if (statuses.length === 0) return;
			queryClient.invalidateQueries({
				predicate: (query) => {
					const queryKey = query.queryKey as [
						string,
						unknown,
						{ status?: string } | undefined,
					];
					if (queryKey[0] !== "commands") return false;
					const filters = queryKey[2];
					if (!filters || typeof filters !== "object") return false;
					const status = (filters as { status?: string }).status;
					return status ? statuses.includes(status as CommandStatus) : false;
				},
			});
		},
		[queryClient],
	);

	const invalidatePurchaseOrderColumns = useCallback(
		(statuses: CommandStatus[]) => {
			const shouldInvalidate = statuses.some(
				(status) =>
					status === CommandStatus.PARCEL_PREPARED ||
					status === CommandStatus.IN_DELIVERY,
			);
			if (!shouldInvalidate) return;
			queryClient.invalidateQueries({
				predicate: (query) => {
					const queryKey = query.queryKey as [string, unknown];
					if (queryKey[0] !== "purchase-orders") return false;
					const filters = queryKey[1];
					if (!filters || typeof filters !== "object") return false;
					const status = (filters as { status?: string }).status;
					return status ? statuses.includes(status as CommandStatus) : false;
				},
			});
		},
		[queryClient],
	);

	// When dropping on WAITING_FOR_MODELING, orders without manager must get one before status change
	const [pendingWaitingForModeling, setPendingWaitingForModeling] = useState<{
		ids: number[];
	} | null>(null);

	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 5,
			},
		}),
	);

	const handleToggleSelect = useCallback((id: number) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) {
				next.delete(id);
			} else {
				// Only add if it has the same status as existing selected cards
				const commandToAdd = commandDataRef.current.get(id);
				if (!commandToAdd) return prev;

				if (next.size === 0) {
					// No existing selection, just add it
					next.add(id);
				} else {
					// Check if all existing selected cards have the same status
					const firstSelectedId = Array.from(next)[0];
					const firstSelectedCommand =
						commandDataRef.current.get(firstSelectedId);
					if (
						firstSelectedCommand &&
						firstSelectedCommand.status === commandToAdd.status
					) {
						next.add(id);
					}
					// If status doesn't match, don't add it
				}
			}
			return next;
		});
	}, []);

	const handleDragStart = useCallback(
		(event: DragStartEvent) => {
			isDraggingRef.current = true;
			const draggedId = Number(event.active.id);
			dragStartRef.current = draggedId;
			setActiveId(draggedId);

			if (selectedIds.has(draggedId)) {
				setDraggedIds(Array.from(selectedIds));
			} else {
				setDraggedIds([draggedId]);
			}
		},
		[selectedIds],
	);

	const handleDragEnd = useCallback(
		(event: DragEndEvent) => {
			const { active, over } = event;

			if (over && typeof over.id === "string") {
				const targetStatus = over.id as CommandStatus;
				let idsToUpdate =
					draggedIds.length > 0 ? draggedIds : [Number(active.id)];

				if (targetStatus === CommandStatus.WAITING_FOR_MODELING) {
					const commands = idsToUpdate
						.map((id) => commandDataRef.current.get(id))
						.filter((cmd): cmd is Command => cmd != null);
					const ordersWithoutModeler = commands.filter((cmd) => !cmd.modelerId);
					if (ordersWithoutModeler.length > 0) {
						setPendingWaitingForModeling({ ids: idsToUpdate });
						setDraggedIds([]);
						setActiveId(null);
						dragStartRef.current = null;
						isDraggingRef.current = false;
						setSelectedIds(new Set());
						return;
					}
				}

				// Only commands with material SILICON can be dropped into INJECTING
				if (targetStatus === CommandStatus.INJECTING) {
					const commands = idsToUpdate
						.map((id) => commandDataRef.current.get(id))
						.filter((cmd): cmd is Command => cmd != null);
					const siliconCommands = commands.filter(
						(cmd) => getCommandMaterialType(cmd) === "SILICONE",
					);
					idsToUpdate = siliconCommands.map((c) => c.id);
					if (idsToUpdate.length === 0) {
						toast.error(
							"Déplacement impossible",
							"Seules les commandes en silicone peuvent être en injection.",
						);
						setDraggedIds([]);
						setActiveId(null);
						dragStartRef.current = null;
						isDraggingRef.current = false;
						setSelectedIds(new Set());
						return;
					}
				}

				if (idsToUpdate.length > 0) {
					const idsSet = new Set(idsToUpdate);
					const updatedCommands = idsToUpdate
						.map((id) => commandDataRef.current.get(id))
						.filter((cmd): cmd is Command => cmd != null)
						.map((cmd) => {
							const next = { ...cmd, status: targetStatus };
							commandDataRef.current.set(cmd.id, next);
							return next;
						});

					const getCommandSortTime = (command: Command) => {
						const gesd = command.groupExpectedShippingDate;
						const esd = command.expectedShippingDate;
						const date = gesd ?? esd;
						return date ? new Date(date).getTime() : 0;
					};
					const compareCommands = (a: Command, b: Command) => {
						if (a.isPriority && !b.isPriority) return -1;
						if (!a.isPriority && b.isPriority) return 1;
						return getCommandSortTime(a) - getCommandSortTime(b);
					};

					queryClient
						.getQueriesData(["commands"])
						.forEach(([queryKey, data]) => {
							const typedData = data as
								| {
										pages: { data: Command[] }[];
										pageParams: unknown[];
								  }
								| undefined;
							if (!typedData?.pages) return;
							if (!Array.isArray(queryKey)) return;

							const filters = queryKey[2] as
								| { status?: CommandStatus }
								| undefined;
							const statusFilter = filters?.status;
							if (!statusFilter) return;

							const shouldAdd = statusFilter === targetStatus;
							const shouldRemove = statusFilter !== targetStatus;

							let changed = false;
							const nextPages = typedData.pages.map((page, pageIndex) => {
								let nextData = page.data;

								if (shouldRemove) {
									const filtered = nextData.filter(
										(cmd) => !idsSet.has(cmd.id),
									);
									if (filtered.length !== nextData.length) {
										changed = true;
										nextData = filtered;
									}
								}

								if (shouldAdd && pageIndex === 0) {
									const existing = new Set(nextData.map((cmd) => cmd.id));
									const toAdd = updatedCommands.filter(
										(cmd) => !existing.has(cmd.id),
									);
									if (toAdd.length > 0) {
										changed = true;
										nextData = [...toAdd, ...nextData].sort(compareCommands);
									}
								}

								return nextData === page.data
									? page
									: { ...page, data: nextData };
							});

							if (changed) {
								queryClient.setQueryData(queryKey, {
									...typedData,
									pages: nextPages,
								});
							}
						});
				}

				bulkChangeStatus({
					ids: idsToUpdate,
					status: targetStatus,
				});

				setSelectedIds(new Set());
			}

			setDraggedIds([]);
			setActiveId(null);
			dragStartRef.current = null;
			isDraggingRef.current = false;
		},
		[draggedIds, bulkChangeStatus, toast, queryClient],
	);

	// Handle card ref registration
	const handleCardRef = useCallback((id: number, node: HTMLElement | null) => {
		if (node) {
			cardRefs.current.set(id, node);
		} else {
			cardRefs.current.delete(id);
		}
	}, []);

	// Handle command data registration
	const handleCommandData = useCallback((command: Command) => {
		commandDataRef.current.set(command.id, command);
	}, []);

	// Calculate which cards intersect with selection box
	const getIntersectingCards = useCallback(
		(box: { startX: number; startY: number; endX: number; endY: number }) => {
			const intersectingIds = new Set<number>();
			const minX = Math.min(box.startX, box.endX);
			const maxX = Math.max(box.startX, box.endX);
			const minY = Math.min(box.startY, box.endY);
			const maxY = Math.max(box.startY, box.endY);

			// First, find all intersecting cards
			const allIntersectingIds = new Set<number>();
			cardRefs.current.forEach((element, id) => {
				const rect = element.getBoundingClientRect();
				const containerRect = containerRef.current?.getBoundingClientRect();
				if (!containerRect) return;

				// Convert to container-relative coordinates
				const cardLeft = rect.left - containerRect.left;
				const cardRight = rect.right - containerRect.left;
				const cardTop = rect.top - containerRect.top;
				const cardBottom = rect.bottom - containerRect.top;

				// Check if card intersects with selection box
				if (
					cardRight >= minX &&
					cardLeft <= maxX &&
					cardBottom >= minY &&
					cardTop <= maxY
				) {
					allIntersectingIds.add(id);
				}
			});

			// Filter to only include cards with the same status
			if (allIntersectingIds.size === 0) {
				return intersectingIds;
			}

			// Determine the status to filter by
			let targetStatus: CommandStatus | null = null;

			// If there are existing selected cards, use their status
			if (selectedIds.size > 0) {
				const firstSelectedId = Array.from(selectedIds)[0];
				const firstSelectedCommand =
					commandDataRef.current.get(firstSelectedId);
				if (firstSelectedCommand) {
					targetStatus = firstSelectedCommand.status;
				}
			}

			// If no target status yet, use the status of the first intersecting card
			if (!targetStatus) {
				const firstIntersectingId = Array.from(allIntersectingIds)[0];
				const firstIntersectingCommand =
					commandDataRef.current.get(firstIntersectingId);
				if (firstIntersectingCommand) {
					targetStatus = firstIntersectingCommand.status;
				}
			}

			// Filter to only include cards with the target status
			if (targetStatus) {
				allIntersectingIds.forEach((id) => {
					const command = commandDataRef.current.get(id);
					if (command && command.status === targetStatus) {
						intersectingIds.add(id);
					}
				});
			}

			return intersectingIds;
		},
		[selectedIds],
	);

	// Handle mouse down for selection box
	const handleMouseDown = useCallback((e: React.MouseEvent) => {
		// Only start selection if:
		// 1. Left mouse button
		// 2. Not already dragging a card
		// 3. Clicking on empty space (not on a card)
		if (e.button === 0 && !isDraggingRef.current) {
			const target = e.target as HTMLElement;
			// Check if click is on a card or within a card
			const isCardClick = target.closest("[data-card-id]") !== null;

			if (!isCardClick) {
				const containerRect = containerRef.current?.getBoundingClientRect();
				if (!containerRect) return;

				const startX = e.clientX - containerRect.left;
				const startY = e.clientY - containerRect.top;

				selectionStartRef.current = { x: startX, y: startY };
				setIsSelecting(true);
				setSelectionBox({
					startX,
					startY,
					endX: startX,
					endY: startY,
				});
			}
		}
	}, []);

	// Handle mouse move for selection box
	const handleMouseMove = useCallback(
		(e: MouseEvent) => {
			if (
				isSelecting &&
				selectionBox &&
				containerRef.current &&
				selectionStartRef.current
			) {
				const containerRect = containerRef.current.getBoundingClientRect();
				const endX = e.clientX - containerRect.left;
				const endY = e.clientY - containerRect.top;

				// Only update if mouse has moved at least 3 pixels to avoid accidental selections
				const deltaX = Math.abs(endX - selectionStartRef.current.x);
				const deltaY = Math.abs(endY - selectionStartRef.current.y);
				if (deltaX < 3 && deltaY < 3) {
					return;
				}

				setSelectionBox({
					...selectionBox,
					endX,
					endY,
				});

				// Update selection based on current box
				const newBox = {
					...selectionBox,
					endX,
					endY,
				};
				const intersectingIds = getIntersectingCards(newBox);
				setSelectedIds(intersectingIds);
			}
		},
		[isSelecting, selectionBox, getIntersectingCards],
	);

	// Handle mouse up to finish selection
	const handleMouseUp = useCallback(
		(e: MouseEvent) => {
			if (isSelecting) {
				// Check if this was just a click (not a drag)
				if (selectionStartRef.current) {
					const containerRect = containerRef.current?.getBoundingClientRect();
					if (containerRect) {
						const endX = e.clientX - containerRect.left;
						const endY = e.clientY - containerRect.top;
						const deltaX = Math.abs(endX - selectionStartRef.current.x);
						const deltaY = Math.abs(endY - selectionStartRef.current.y);

						// If mouse didn't move much (just a click), clear selection
						if (deltaX < 3 && deltaY < 3) {
							// Check if click was outside any card
							const target = e.target as HTMLElement;
							const isCardClick = target.closest("[data-card-id]") !== null;

							if (!isCardClick) {
								setSelectedIds(new Set());
							}
						}
					}
				}

				setIsSelecting(false);
				setSelectionBox(null);
				selectionStartRef.current = null;
			}
		},
		[isSelecting],
	);

	// Set up global mouse event listeners for selection
	useEffect(() => {
		if (isSelecting) {
			document.addEventListener("mousemove", handleMouseMove);
			document.addEventListener("mouseup", handleMouseUp);
			return () => {
				document.removeEventListener("mousemove", handleMouseMove);
				document.removeEventListener("mouseup", handleMouseUp);
			};
		}
	}, [isSelecting, handleMouseMove, handleMouseUp]);

	useEffect(() => {
		const handleStatusChanged = (payload: CommandStatusChangedPayload) => {
			const statusesToRefresh = new Set<CommandStatus>();
			if (isCommandStatus(payload.prevStatus)) {
				statusesToRefresh.add(payload.prevStatus);
			}
			if (isCommandStatus(payload.nextStatus)) {
				statusesToRefresh.add(payload.nextStatus);
			}
			if (
				statusesToRefresh.size === 0 &&
				isCommandStatus(payload.currentStatus)
			) {
				statusesToRefresh.add(payload.currentStatus);
			}
			const statuses = Array.from(statusesToRefresh);
			invalidateCommandColumns(statuses);
			invalidatePurchaseOrderColumns(statuses);
			queryClient.invalidateQueries({
				queryKey: ["commands", "stats", "status"],
			});
			queryClient.invalidateQueries({
				queryKey: ["commands", "stats", "status", "halted"],
			});
		};

		socket.on("command:status_changed", handleStatusChanged);
		return () => {
			socket.off("command:status_changed", handleStatusChanged);
		};
	}, [invalidateCommandColumns, invalidatePurchaseOrderColumns, queryClient]);

	return (
		<Flex direction="column" gap={4}>
			<OrdersKanbanToolbar />
			<OrdersSubToolbar />
			<DndContext
				sensors={sensors}
				onDragStart={handleDragStart}
				onDragEnd={handleDragEnd}
			>
				<Box
					ref={containerRef}
					className="flex flex-row min-h-[80vh] max-w-full overflow-x-scroll gap-4 relative"
					onMouseDown={handleMouseDown}
				>
					{/* Orders Columns */}
					{getScopeColumns().map((column) => (
						<OrdersKanbanOrdersColumn
							key={column}
							status={column}
							selectedIds={selectedIds}
							onToggleSelect={handleToggleSelect}
							onCardRef={handleCardRef}
							onCommandData={handleCommandData}
							draggedIds={draggedIds}
						/>
					))}
					{/* Delivery */}
					{["secretariat", "global"].includes(scope) && (
						<OrdersKanbanPurchaseOrdersColumn
							status={CommandStatus.PARCEL_PREPARED}
						/>
					)}
					{["secretariat", "global"].includes(scope) && (
						<OrdersKanbanPurchaseOrdersColumn
							status={CommandStatus.IN_DELIVERY}
						/>
					)}
					{["global"].includes(scope) && (
						<OrdersKanbanOrdersColumn
							status={CommandStatus.DELIVERED}
							selectedIds={selectedIds}
							onToggleSelect={handleToggleSelect}
							onCardRef={handleCardRef}
							onCommandData={handleCommandData}
							draggedIds={draggedIds}
						/>
					)}
					{/* Selection box overlay */}
					{selectionBox &&
						(() => {
							const container = containerRef.current;
							const scrollLeft = container?.scrollLeft ?? 0;

							const left =
								Math.min(selectionBox.startX, selectionBox.endX) + scrollLeft;
							const top = Math.min(selectionBox.startY, selectionBox.endY);
							const width = Math.abs(selectionBox.endX - selectionBox.startX);
							const height = Math.abs(selectionBox.endY - selectionBox.startY);

							return (
								<div
									className="absolute border-2 border-blue-500 bg-brand-500 opacity-40 pointer-events-none z-50"
									style={{
										left,
										top,
										width,
										height,
									}}
								/>
							);
						})()}
				</Box>
				<DragOverlay dropAnimation={null}>
					{activeId && draggedIds.length > 0 ? (
						<div className="flex flex-col gap-1">
							{draggedIds.map((id) => {
								const command = commandDataRef.current.get(id);
								if (!command) return null;
								return (
									<OrdersKanbanCard
										key={id}
										isSelected={false}
										isDragging={false}
										onToggleSelect={() => {}}
										card={{
											command,
										}}
									/>
								);
							})}
						</div>
					) : null}
				</DragOverlay>
			</DndContext>
			{pendingWaitingForModeling && (
				<ChangeManagerOrderModal
					orderId={0}
					isOpen={true}
					onOpen={() => {}}
					onClose={() => setPendingWaitingForModeling(null)}
					orderIds={pendingWaitingForModeling.ids}
					targetStatus={CommandStatus.WAITING_FOR_MODELING}
					onSuccess={() => setPendingWaitingForModeling(null)}
				/>
			)}
		</Flex>
	);
};

export default OrdersKanbanView;
